import React, { FunctionComponent } from 'react'
import { DragSourceMonitor } from 'react-dnd'
import { concat, indexOf, isNil } from 'lodash'
import { TableBody as MuiTableBody } from '@material-ui/core'
import { CustomRender } from './Table'
import TableRow from './TableRow'
import { Schema } from '../../api/RcfactoryApi'

interface Props {
  customRenders?: CustomRender[],
  data: Record<string, unknown>[],
  editableProperties?: string[],
  idProperty?: string,
  keys: string[],
  multiSelect?: boolean,
  onPropertyChange?: (changes: {
    data: Record<string, unknown>,
    property: string,
    value: unknown
  }[]) => void,
  onRowDrag?: (
    item: { id: number } | undefined,
    monitor: DragSourceMonitor,
    data: Record<string, unknown>
  ) => void,
  onSelect?: (ids: (number | string | null)[]) => void,
  rowDragType?: string,
  schema?: Schema,
  selected?: (number | string | null)[] | null
}

export const updateSelected = (
  id: number | string,
  selected?: (number | string | null)[] | null,
  multiSelect?: boolean
): (number | string | null)[] => {
  if (isNil(selected)) {
    return [id]
  }
  let newSelected: (number | string | null)[] = []
  const selectedIndex = indexOf(selected, id)
  if (multiSelect) {
    if (selectedIndex === -1) {
      newSelected = concat(selected, id)
    } else if (selectedIndex === 0) {
      newSelected = concat(selected.slice(1))
    } else if (selectedIndex === selected.length - 1) {
      newSelected = concat(selected.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      )
    }
  } else if (selectedIndex === -1) {
    newSelected = [id]
  }
  return newSelected
}

const TableBody: FunctionComponent<Props> = (props: Props) => {
  const {
    customRenders,
    data,
    editableProperties,
    idProperty,
    keys,
    multiSelect,
    onPropertyChange,
    onRowDrag,
    onSelect,
    rowDragType,
    schema,
    selected
  } = props

  const handleSelectClick = (id: number | string) => {
    if (!onSelect) {
      throw Error('Cannot handleSelectClick if setSelected is undefined!')
    }
    onSelect(updateSelected(id, selected, multiSelect))
  }

  const isSelected = (id: number | string) => {
    if (!selected) {
      return false
    }
    return indexOf(selected, id) !== -1
  }

  return (
    <MuiTableBody>
      {data.map((row, index) => {
        const isItemSelected = idProperty ? isSelected((row[idProperty]) as string | number) : false

        return (
          <TableRow
            customRenders={customRenders}
            data={row}
            dragType={rowDragType}
            editableProperties={editableProperties}
            index={index}
            idProperty={idProperty}
            isSelected={isItemSelected}
            key={index}
            keys={keys}
            multiSelect={multiSelect}
            onDrag={onRowDrag}
            onPropertyChange={onPropertyChange}
            onSelectClick={onSelect ? handleSelectClick : undefined}
            schema={schema}
          />
        )
      })}
    </MuiTableBody>
  )
}

export default TableBody
